import { SupportedLanguage } from '@foundation/localization';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useState, JSX } from 'react';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import Logger, { LoggerOrigin } from '@/services/Logger';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import WorldpayJCBService from '@/services/worldpay/WorldpayJCBService';
import { redirect } from '@/utils/redirect';

interface IPayWithWorldpayJCBBtnProps {
  readonly navigation?: JSX.Element | null;
}

export function PayWithWorldpayJCBBtn({
  navigation,
}: IPayWithWorldpayJCBBtnProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const { accessToken, userId } = useAuth();

  useEffect(() => {
    setLoading(true);
    setError(undefined);

    let promise;
    if (PaymentStore.flow === PaymentIntentFlow.Manage) {
      const customerNumber = userId;
      if (customerNumber === undefined) {
        setError(new Error('customerNumber is undefined!'));
        return;
      }

      if (PaymentStore.legalEntity === undefined) {
        setError(new Error('Legal entity cannot be undefined'));
        return;
      }

      promise = WorldpayJCBService.getManageRedirectUrl(
        PaymentStore.legalEntity,
        customerNumber,
        accessToken ?? '',
        language as SupportedLanguage,
      );
    } else {
      promise = WorldpayJCBService.getRedirectUrl(
        language as SupportedLanguage,
        accessToken ?? '',
      );
    }

    promise
      .then(setRedirectUrl)
      .catch((e: Error) => {
        TelemetryService.trackException(e, 'WorldpayJCBBtn');
        Logger.error(LoggerOrigin.ReactComponent, 'WPJCB URL fetch failure', e);
        setError(e);
      })
      .finally(() => setLoading(false));
  }, [accessToken, language, userId]);

  if (error) {
    return (
      <AlertWarning
        title={t('Something went wrong!')}
        description={t('Card payments are currently unavailable.')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return (
    <div className="flex space-x-2">
      {navigation}
      <PayButton
        onClick={() => redirect(redirectUrl)}
        title={
          PaymentStore.flow === PaymentIntentFlow.Manage
            ? t('Add new card')
            : t('Proceed to pay')
        }
      />
    </div>
  );
}
